import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { QrCode } from '../nursery/QrCode';
import { usePolling } from '../presentation/usePolling';
import { useMe } from '../session/SessionProvider';
import { BibleTabs } from './Atlas';
import { bibleLink } from './BiblePage';

type Level = 'ninos' | 'general' | 'avanzado';
const LEVELS: [Level, string][] = [
  ['ninos', 'Niños'],
  ['general', 'General'],
  ['avanzado', 'Jóvenes y avanzado'],
];
const LEVEL_INFO: Record<Level, { icon: string; hint: string }> = {
  ninos: { icon: '🌱', hint: 'Historias que todos conocen' },
  general: { icon: '📖', hint: 'Para toda la congregación' },
  avanzado: { icon: '⛰️', hint: 'Para quien estudia a fondo' },
};
const LETTERS = ['A', 'B', 'C', 'D'];
/** El bono de rapidez se acaba a los 20 segundos (100 + hasta 100 puntos por pregunta). */
const BONUS_SECONDS = 20;
type Played = { id: string; question: string; options: string[]; answer: number; ref: string | null };
type MyStats = { items: { level: string; games: number; best: number; correct: number; total: number }[]; bankSize: number; streak?: number; playedToday?: boolean; hidden?: boolean };

export function TriviaPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [playing, setPlaying] = useState<Level | null>(null);
  const [boardKey, setBoardKey] = useState(0);
  const [round, setRound] = useState(0);
  const stats = useAsync((signal) => api.get<MyStats>('/bible/me/trivia', signal), []);
  const data = stats.status === 'ready' ? stats.data : null;
  if (playing) return <Practice key={round} level={playing} onExit={() => (setPlaying(null), stats.reload(), setBoardKey((k) => k + 1))} onAgain={() => setRound((r) => r + 1)} />;
  const streak = data?.streak ?? 0;
  const best = Math.max(0, ...(data?.items.map((s) => s.best) ?? []));
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Trivia bíblica</h1>
      </header>
      <BibleTabs active="trivia" />
      <section className="trivia-hero" aria-label="Practicar">
        <div className="trivia-hero__stats">
          <div className={`trivia-hero__stat${streak > 0 ? ' trivia-hero__stat--fire' : ''}`}>
            <strong>{streak > 0 ? `🔥 ${streak}` : '0'}</strong>
            <span>{streak === 1 ? 'día seguido' : 'días seguidos'}</span>
          </div>
          <div className="trivia-hero__stat">
            <strong>{best}</strong>
            <span>tu récord</span>
          </div>
          <div className="trivia-hero__stat">
            <strong>{data?.bankSize ?? '…'}</strong>
            <span>preguntas</span>
          </div>
        </div>
        <p className="trivia-hero__lead">
          {data?.playedToday ? '¡Ya jugaste hoy! Cada partida suma para la tabla de tu iglesia.' : streak > 0 ? 'Juega hoy para no perder tu racha.' : '10 preguntas. Entre más rápido contestes bien, más puntos.'}
        </p>
        <div className="trivia-levels">
          {LEVELS.map(([l, name]) => {
            const st = data?.items.find((x) => x.level === l);
            return (
              <button key={l} type="button" className={`trivia-level trivia-level--${l}`} onClick={() => setPlaying(l)}>
                <span className="trivia-level__icon" aria-hidden="true">
                  {LEVEL_INFO[l].icon}
                </span>
                <span className="trivia-level__name">{name}</span>
                <span className="trivia-level__hint">{st ? `Récord ${st.best} · ${Math.round((st.correct / Math.max(1, st.total)) * 100)}% de aciertos` : LEVEL_INFO[l].hint}</span>
              </button>
            );
          })}
        </div>
      </section>
      {churchId ? <Leaderboard key={boardKey} churchId={churchId} hidden={data?.hidden ?? false} onHidden={() => stats.reload()} /> : null}
      {churchId ? <NewGameCard churchId={churchId} /> : null}
      <JoinCodeCard />
      {churchId && me.can('devotional.manage') ? <ChurchQuestionsCard churchId={churchId} /> : null}
    </div>
  );
}

type Board = { period: 'week' | 'all'; players: number; items: BoardRow[]; me: BoardRow | null };
type BoardRow = { place: number; name: string; points: number; games: number; accuracy: number; me: boolean; hidden: boolean };
const MEDALS = ['🥇', '🥈', '🥉'];

/** Tabla de posiciones de la iglesia: puntos de práctica de esta semana o de siempre. */
function Leaderboard({ churchId, hidden, onHidden }: { churchId: string; hidden: boolean; onHidden: () => void }) {
  const [period, setPeriod] = useState<'week' | 'all'>('week');
  const board = useAsync((signal) => api.get<Board>(`/bible/churches/${churchId}/trivia/leaderboard?period=${period}`, signal), [churchId, period]);
  const [saving, setSaving] = useState(false);
  if (board.status === 'error') return null;
  const b = board.status === 'ready' ? board.data : null;
  const podium = b?.items.filter((r) => r.place <= 3).slice(0, 3) ?? [];
  const rest = b?.items.slice(podium.length) ?? [];
  async function toggle(next: boolean) {
    setSaving(true);
    try {
      await api.put('/bible/me/trivia/prefs', { hidden: next });
      onHidden();
      board.reload();
    } finally {
      setSaving(false);
    }
  }
  return (
    <Card title="Tabla de posiciones">
      <div className="trivia-board__tabs" role="group" aria-label="Periodo">
        {(
          [
            ['week', 'Esta semana'],
            ['all', 'General'],
          ] as const
        ).map(([p, label]) => (
          <button key={p} type="button" className={`chip${period === p ? ' chip--active' : ''}`} aria-pressed={period === p} onClick={() => setPeriod(p)}>
            {label}
          </button>
        ))}
      </div>
      {!b ? (
        <Loading />
      ) : b.items.length === 0 ? (
        <p className="trivia-board__empty">{period === 'week' ? 'Nadie ha jugado esta semana. ¡Sé el primero en la tabla!' : 'Todavía nadie ha jugado. ¡Sé el primero en la tabla!'}</p>
      ) : (
        <>
          <ol className="trivia-podium">
            {podium.map((r) => (
              <li key={`${r.place}-${r.name}`} className={`trivia-podium__spot trivia-podium__spot--${r.place}${r.me ? ' trivia-podium__spot--me' : ''}`}>
                <span className="trivia-podium__medal" aria-label={`Lugar ${r.place}`}>
                  {MEDALS[r.place - 1]}
                </span>
                <span className="trivia-podium__name">{r.me ? `${r.name} (tú)` : r.name}</span>
                <span className="trivia-podium__points">{r.points.toLocaleString('es-MX')} pts</span>
                <span className="trivia-podium__step" aria-hidden="true">
                  {r.place}
                </span>
              </li>
            ))}
          </ol>
          {rest.length ? (
            <ol className="standings">
              {rest.map((r) => (
                <li key={`${r.place}-${r.name}`} className={`standings__row${r.me ? ' standings__row--me' : ''}`}>
                  <span className="standings__place">{r.place}</span>
                  <span className="standings__name">{r.me ? `${r.name} (tú)` : r.name}</span>
                  <span className="standings__points">
                    <strong>{r.points.toLocaleString('es-MX')}</strong> <span className="muted small">pts</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
          {b.me && !b.items.some((r) => r.me) ? (
            <p className="trivia-board__mine">
              Vas en el lugar <strong>{b.me.place}</strong> de {b.players} con {b.me.points.toLocaleString('es-MX')} puntos.
            </p>
          ) : null}
        </>
      )}
      <label className="trivia-board__hide small">
        <input type="checkbox" checked={!hidden} disabled={saving} onChange={(e) => void toggle(!e.target.checked)} /> Aparecer con mi nombre en la tabla de mi iglesia
      </label>
      <p className="small muted">Suman los puntos de cada práctica. La semana empieza el lunes. {hidden ? 'Solo tú ves tu lugar.' : ''}</p>
    </Card>
  );
}

function Practice({ level, onExit, onAgain }: { level: Level; onExit: () => void; onAgain: () => void }) {
  const data = useAsync((signal) => api.get<{ questions: Played[] }>(`/bible/trivia/practice?level=${level}&count=10`, signal), [level]);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState({ correct: 0, points: 0, combo: 0, bestCombo: 0 });
  const [gain, setGain] = useState(0);
  const [saved, setSaved] = useState<number | null>(null);
  const [streak, setStreak] = useState<number | null>(null);
  const started = useRef(Date.now());
  useEffect(() => {
    started.current = Date.now();
  }, [i, data.status]);
  const total = data.status === 'ready' ? data.data.questions.length : 0;
  const over = total > 0 && i >= total;
  useEffect(() => {
    if (!over) return;
    void api
      .post<MyStats>('/bible/me/trivia/scores', { level, correct: score.correct, total, points: score.points })
      .then((r) => (setSaved(r.items.find((x) => x.level === level)?.best ?? 0), setStreak(r.streak ?? null)))
      .catch(() => undefined);
  }, [over]); // eslint-disable-line react-hooks/exhaustive-deps
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const qs = data.data.questions;
  const q = qs[i];
  if (!q) {
    const ratio = score.correct / Math.max(1, qs.length);
    const stars = ratio >= 0.9 ? 3 : ratio >= 0.6 ? 2 : ratio >= 0.3 ? 1 : 0;
    const record = saved !== null && saved <= score.points && score.points > 0;
    return (
      <div className="page page--narrow bible-wide">
        <section className={`trivia-final${record ? ' trivia-final--record' : ''}`}>
          {record ? <Confetti /> : null}
          <p className="trivia-final__stars" aria-label={`${stars} de 3 estrellas`}>
            {[0, 1, 2].map((k) => (
              <span key={k} className={k < stars ? 'on' : ''}>
                ★
              </span>
            ))}
          </p>
          <p className="trivia__final">
            {score.correct} de {qs.length} correctas · {score.points} puntos
          </p>
          <p className="trivia-final__msg">{stars === 3 ? '¡Excelente! Conoces bien la Palabra.' : stars === 2 ? '¡Muy bien! Sigue así.' : stars === 1 ? 'Buen intento. Cada partida aprendes algo nuevo.' : 'No te desanimes: lee las citas y vuelve a intentarlo.'}</p>
          {score.bestCombo >= 3 ? <p className="small">Mejor racha de aciertos: 🔥 {score.bestCombo} seguidas</p> : null}
          {saved !== null ? <p className="trivia-final__record">{record ? '🏆 ¡Nuevo récord!' : `Tu récord: ${saved} puntos`}</p> : null}
          {streak ? <p className="small">Llevas {streak} {streak === 1 ? 'día' : 'días seguidos'} jugando.</p> : null}
          <div className="actions actions--wrap trivia-final__actions">
            <Button onClick={onAgain}>Jugar otra vez</Button>
            <Button variant="secondary" onClick={onExit}>
              Volver
            </Button>
          </div>
        </section>
      </div>
    );
  }
  const right = picked !== null && picked === q.answer;
  function choose(k: number) {
    if (picked !== null) return;
    setPicked(k);
    if (k === q!.answer) {
      const secs = (Date.now() - started.current) / 1000;
      const pts = 100 + Math.max(0, Math.round(100 - secs * 5));
      setGain(pts);
      setScore((s) => ({ correct: s.correct + 1, points: s.points + pts, combo: s.combo + 1, bestCombo: Math.max(s.bestCombo, s.combo + 1) }));
    } else {
      setGain(0);
      setScore((s) => ({ ...s, combo: 0 }));
    }
  }
  return (
    <div className="page page--narrow bible-wide trivia-run">
      <div className="trivia-run__top">
        <span className="trivia-run__level">
          {LEVEL_INFO[level].icon} {LEVELS.find(([l]) => l === level)?.[1]}
        </span>
        <span className="trivia-run__score" aria-live="polite">
          {score.combo >= 2 ? <span className="trivia-run__combo">🔥 {score.combo}</span> : null}
          <strong>{score.points}</strong> pts
        </span>
      </div>
      <div className="trivia-run__dots" aria-label={`Pregunta ${i + 1} de ${qs.length}`}>
        {qs.map((_, k) => (
          <span key={k} className={k < i ? 'done' : k === i ? 'now' : ''} />
        ))}
      </div>
      <p className="muted small">
        Pregunta {i + 1} de {qs.length} · {score.points} puntos
      </p>
      <div className="card trivia-run__card">
        {picked === null ? <div key={i} className="trivia-run__bonus" style={{ animationDuration: `${BONUS_SECONDS}s` }} aria-hidden="true" /> : null}
        <p className="trivia__question">{q.question}</p>
        <div className="trivia__options">
          {q.options.map((o, k) => (
            <button
              key={k}
              type="button"
              className={`trivia__option trivia__option--${k}${picked !== null && k === q.answer ? ' trivia__option--right' : ''}${picked === k && k !== q.answer ? ' trivia__option--wrong' : ''}${picked !== null && picked !== k && k !== q.answer ? ' trivia__option--dim' : ''}`}
              onClick={() => choose(k)}
              disabled={picked !== null}
            >
              <span className="trivia__letter">{LETTERS[k]}</span> {o}
            </button>
          ))}
        </div>
        {picked !== null ? (
          <div className={`trivia-run__result${right ? ' trivia-run__result--ok' : ''}`}>
            <p className={right ? 'trivia__ok' : 'trivia__bad'}>
              {right ? '¡Correcto!' : `La respuesta es: ${q.options[q.answer]}`}
              {right ? <span className="trivia-run__gain"> +{gain}</span> : null}
              {q.ref ? (
                <>
                  {' '}
                  · <Link to={bibleLink(q.ref)}>{q.ref}</Link>
                </>
              ) : null}
            </p>
            <Button onClick={() => (setPicked(null), setI(i + 1))}>{i + 1 < qs.length ? 'Siguiente' : 'Ver resultado'}</Button>
          </div>
        ) : null}
      </div>
      <button type="button" className="btn btn--ghost" onClick={onExit}>
        Salir
      </button>
    </div>
  );
}

/** Confeti de puro CSS para el nuevo récord (no se anima si la persona pidió menos movimiento). */
function Confetti() {
  return (
    <div className="trivia-confetti" aria-hidden="true">
      {Array.from({ length: 18 }, (_, k) => (
        <span key={k} style={{ left: `${(k * 53) % 100}%`, animationDelay: `${(k % 6) * 0.12}s` }} />
      ))}
    </div>
  );
}

/** «¿Te invitaron a jugar?»: el código se escribe aquí mismo. */
function JoinCodeCard() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  return (
    <Card title="¿Te invitaron a jugar?">
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (/^\d{6}$/.test(code)) navigate(`/trivia/${code}`);
        }}
      >
        <Field label="Código del juego" name="code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" hint="El código de 6 números que aparece en la pantalla." />
        <Button type="submit" disabled={code.length !== 6}>
          Entrar
        </Button>
      </form>
    </Card>
  );
}

function NewGameCard({ churchId }: { churchId: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [teams, setTeams] = useState(['Leones', 'Águilas']);
  const [error, setError] = useState<string | null>(null);
  const [book, setBook] = useState('');
  // Trivia bíblica de un libro: se puede estudiar un libro y concursar solo con preguntas de ese libro.
  const books = useAsync(
    (signal) => (open ? api.get<{ items: { book: string; count: number }[] }>(`/bible/churches/${churchId}/trivia/books`, signal).then((r) => r.items).catch(() => []) : Promise.resolve([])),
    [churchId, open],
  );
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: string }>(`/bible/churches/${churchId}/trivia/games`, {
        title: String(f.get('title') || 'Trivia bíblica'),
        level: String(f.get('level')),
        count: Number(f.get('count') || 10),
        seconds: Number(f.get('seconds') || 20),
        teams: teams.filter((t) => t.trim()),
        book: book || null,
      });
      navigate(`/app/trivia/juego/${r.id}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Competencia en vivo por equipos">
      <p className="small muted">Proyecta la pregunta; cada quien contesta desde su celular con un código, sin necesidad de cuenta. Ideal para jóvenes y niños.</p>
      {!open ? (
        <Button onClick={() => setOpen(true)}>Crear juego</Button>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          {error ? <Alert>{error}</Alert> : null}
          <Field label="Nombre del juego" name="title" defaultValue="Trivia bíblica" maxLength={80} />
          <div className="field">
            <label htmlFor="trivia-book">Libro (trivia de un solo libro)</label>
            <select id="trivia-book" value={book} onChange={(e) => setBook(e.target.value)}>
              <option value="">De toda la Biblia</option>
              {(books.status === 'ready' ? books.data : []).map((b) => (
                <option key={b.book} value={b.book}>
                  {b.book} ({b.count} {b.count === 1 ? 'pregunta' : 'preguntas'})
                </option>
              ))}
            </select>
            <small className="field__hint">Con un libro se usan sus preguntas de todos los niveles. Agrega las tuyas con su cita en «Preguntas de mi iglesia».</small>
          </div>
          <div className="field" hidden={Boolean(book)}>
            <label htmlFor="trivia-level">Nivel</label>
            <select id="trivia-level" name="level" defaultValue="general">
              {LEVELS.map(([l, n]) => (
                <option key={l} value={l}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="form__row">
            <Field label="Preguntas" name="count" type="number" min={3} max={30} defaultValue={10} />
            <Field label="Segundos por pregunta" name="seconds" type="number" min={5} max={120} defaultValue={20} />
          </div>
          <fieldset className="field">
            <legend>Equipos</legend>
            {teams.map((t, i) => (
              <div key={i} className="code-row">
                <input aria-label={`Equipo ${i + 1}`} value={t} maxLength={30} onChange={(e) => setTeams(teams.map((x, j) => (j === i ? e.target.value : x)))} />
                {teams.length > 1 ? (
                  <Button type="button" variant="ghost" onClick={() => setTeams(teams.filter((_, j) => j !== i))} aria-label={`Quitar ${t}`}>
                    ✕
                  </Button>
                ) : null}
              </div>
            ))}
            {teams.length < 8 ? (
              <Button type="button" variant="ghost" onClick={() => setTeams([...teams, `Equipo ${teams.length + 1}`])}>
                + Agregar equipo
              </Button>
            ) : null}
          </fieldset>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Crear y proyectar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function ChurchQuestionsCard({ churchId }: { churchId: string }) {
  const base = `/bible/churches/${churchId}/trivia/questions`;
  const list = useAsync((signal) => api.get<{ items: { id: string; level: string; question: string; answer: string }[] }>(base, signal), [base]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setError(null);
    try {
      await api.post(base, {
        level: String(f.get('level')),
        question: String(f.get('question')),
        answer: String(f.get('answer')),
        wrong: [String(f.get('w1')), String(f.get('w2')), String(f.get('w3'))],
        ref: String(f.get('ref') || '') || null,
      });
      form.reset();
      list.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  return (
    <Card title="Preguntas de nuestra iglesia" action={!open ? <Button variant="ghost" onClick={() => setOpen(true)}>Agregar</Button> : undefined}>
      <p className="small muted">Se mezclan con las preguntas de KEORA en la práctica y en los juegos de tu iglesia.</p>
      {open ? (
        <form className="form" onSubmit={add} noValidate>
          {error ? <Alert>{error}</Alert> : null}
          <div className="field">
            <label htmlFor="tq-level">Nivel</label>
            <select id="tq-level" name="level" defaultValue="general">
              {LEVELS.map(([l, n]) => (
                <option key={l} value={l}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <Field label="Pregunta" name="question" required maxLength={300} />
          <Field label="Respuesta correcta" name="answer" required maxLength={150} />
          <Field label="Opción incorrecta 1" name="w1" required maxLength={150} />
          <Field label="Opción incorrecta 2" name="w2" required maxLength={150} />
          <Field label="Opción incorrecta 3" name="w3" required maxLength={150} />
          <Field label="Cita (opcional)" name="ref" maxLength={60} placeholder="Juan 3:16" />
          <div className="actions">
            <Button type="submit">Guardar pregunta</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
          </div>
        </form>
      ) : null}
      {list.status === 'ready' && list.data.items.length ? (
        <ul className="list">
          {list.data.items.map((q) => (
            <li key={q.id} className="list__item">
              {q.question} <span className="muted small">→ {q.answer}</span>{' '}
              <button type="button" className="btn btn--ghost" onClick={() => void api.delete(`${base}/${q.id}`).then(list.reload)}>
                Quitar
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

// ── Pantalla del líder (se proyecta) ────────────────────────────────────────

type HostState = {
  id: string; code: string; title: string; status: 'lobby' | 'question' | 'reveal' | 'finished'; current: number; total: number; seconds: number; remainingMs: number;
  question: { text: string; options: string[]; ref: string | null; answer: number | null } | null; answered: number; players: number;
  playerNames: { name: string; team: string }[]; distribution: number[] | null;
  teams: { name: string; players: number; points: number; average: number }[]; top: { name: string; team: string; points: number }[];
};

export function TriviaHostPage() {
  const { gameId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId ?? '';
  const url = `/bible/churches/${churchId}/trivia/games/${gameId}`;
  const state = usePolling((signal) => api.get<HostState>(url, signal), 1000, [url]);
  const [busy, setBusy] = useState(false);
  const advancing = useRef(-2);
  const s = state.data;
  async function next() {
    setBusy(true);
    try {
      await api.post(`${url}/next`);
      state.refresh();
    } finally {
      setBusy(false);
    }
  }
  // Se revela solo cuando se acaba el tiempo o ya contestaron todos.
  useEffect(() => {
    if (s?.status === 'question' && (s.remainingMs <= 0 || (s.players > 0 && s.answered >= s.players)) && advancing.current !== s.current) {
      advancing.current = s.current;
      void next();
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return state.error ? <ErrorState error={state.error} /> : <Loading />;
  const joinUrl = `${window.location.origin}/trivia/${s.code}`;
  return (
    <div className="trivia-host">
      <header className="trivia-host__bar">
        <Link to="/app/biblia/trivia">← Salir</Link>
        <strong>{s.title}</strong>
        <span>
          Código: <strong className="trivia-host__code">{s.code}</strong>
        </span>
        <Button variant="ghost" onClick={() => void document.documentElement.requestFullscreen?.().catch(() => undefined)}>
          Pantalla completa
        </Button>
      </header>
      {s.status === 'lobby' ? (
        <div className="trivia-host__lobby">
          <div>
            <p className="trivia-host__big">
              Entra a <strong>mykeora.com/trivia</strong>
              <br />y escribe el código <strong className="trivia-host__code">{s.code}</strong>
            </p>
            <div className="trivia-host__qr">
              <QrCode value={joinUrl} label={`Código QR para entrar al juego ${s.code}`} />
            </div>
          </div>
          <div>
            <p className="trivia-host__big">{s.players} {s.players === 1 ? 'jugador' : 'jugadores'}</p>
            <ul className="chips">
              {s.playerNames.map((p, i) => (
                <li key={i} className="chip">
                  {p.name} · {p.team}
                </li>
              ))}
            </ul>
            <Button onClick={() => void next()} busy={busy} disabled={s.players === 0}>
              Empezar
            </Button>
          </div>
        </div>
      ) : null}
      {(s.status === 'question' || s.status === 'reveal') && s.question ? (
        <div className="trivia-host__round">
          <p className="muted">
            Pregunta {s.current + 1} de {s.total}
            {s.status === 'question' ? ` · ${Math.ceil(s.remainingMs / 1000)} s · ${s.answered}/${s.players} contestaron` : ''}
          </p>
          <p className="trivia-host__question">{s.question.text}</p>
          <div className="trivia__options trivia__options--big">
            {s.question.options.map((o, k) => (
              <div key={k} className={`trivia__option trivia__option--${k}${s.question!.answer === k ? ' trivia__option--right' : ''}${s.status === 'reveal' && s.question!.answer !== k ? ' trivia__option--dim' : ''}`}>
                <span className="trivia__letter">{LETTERS[k]}</span> {o}
                {s.distribution ? <span className="trivia__count">{s.distribution[k]}</span> : null}
              </div>
            ))}
          </div>
          {s.status === 'reveal' ? (
            <>
              {s.question.ref ? <p className="muted">{s.question.ref}</p> : null}
              <Scoreboard teams={s.teams} top={s.top.slice(0, 5)} />
              <Button onClick={() => void next()} busy={busy}>
                {s.current + 1 < s.total ? 'Siguiente pregunta' : 'Ver resultados'}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => void next()} busy={busy}>
              Cerrar pregunta
            </Button>
          )}
        </div>
      ) : null}
      {s.status === 'finished' ? (
        <div className="trivia-host__round">
          <p className="trivia-host__question">🏆 ¡Ganó {s.teams[0]?.name ?? ''}!</p>
          <Scoreboard teams={s.teams} top={s.top} />
        </div>
      ) : null}
    </div>
  );
}

function Scoreboard({ teams, top }: { teams: HostState['teams']; top: HostState['top'] }) {
  return (
    <div className="trivia-score">
      <ol className="trivia-score__teams">
        {teams.map((t) => (
          <li key={t.name}>
            <strong>{t.name}</strong> · {t.average} pts por jugador <span className="muted small">({t.players} jugadores, {t.points} en total)</span>
          </li>
        ))}
      </ol>
      {top.length ? (
        <ol className="trivia-score__top">
          {top.map((p, i) => (
            <li key={i}>
              {p.name} <span className="muted small">({p.team})</span> · {p.points}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

// ── Jugadores (sin cuenta) ─────────────────────────────────────────────────

const TOKEN_KEY = (code: string) => `keora.trivia.${code}`;

export function TriviaJoinPage() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam ?? '');
  const lobby = useAsync(
    (signal) => (/^\d{6}$/.test(codeParam ?? '') ? api.get<{ title: string; teams: string[]; status: string }>(`/bible/public/trivia/${codeParam}`, signal) : Promise.resolve(null)),
    [codeParam],
  );
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!codeParam) return;
    try {
      const t = localStorage.getItem(TOKEN_KEY(codeParam));
      if (t) navigate(`/trivia/jugar/${t}`, { replace: true });
    } catch {
      /* sin almacenamiento */
    }
  }, [codeParam, navigate]);
  async function join(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setError(null);
    try {
      const r = await api.post<{ token: string }>(`/bible/public/trivia/${codeParam}/join`, { name: String(f.get('name')), team: Number(f.get('team')) });
      try {
        localStorage.setItem(TOKEN_KEY(codeParam!), r.token);
      } catch {
        /* sin almacenamiento */
      }
      navigate(`/trivia/jugar/${r.token}`);
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  return (
    <div className="trivia-play">
      <h1>Trivia bíblica</h1>
      {!codeParam ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (/^\d{6}$/.test(code)) navigate(`/trivia/${code}`);
          }}
        >
          <Field label="Código del juego" name="code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" />
          <Button type="submit" disabled={code.length !== 6}>
            Entrar
          </Button>
        </form>
      ) : lobby.status === 'loading' ? (
        <Loading />
      ) : lobby.status === 'error' ? (
        <>
          <Alert>No hay un juego con ese código.</Alert>
          <Link to="/trivia">Probar otro código</Link>
        </>
      ) : lobby.data ? (
        <form className="form" onSubmit={join}>
          <p>
            <strong>{lobby.data.title}</strong>
          </p>
          {error ? <Alert>{error}</Alert> : null}
          <Field label="Tu nombre" name="name" required maxLength={40} autoComplete="given-name" />
          <fieldset className="field">
            <legend>Tu equipo</legend>
            <div className="chips">
              {lobby.data.teams.map((t, i) => (
                <label key={t} className="chip">
                  <input type="radio" name="team" value={i} defaultChecked={i === 0} /> {t}
                </label>
              ))}
            </div>
          </fieldset>
          <Button type="submit">¡A jugar!</Button>
        </form>
      ) : null}
    </div>
  );
}

type PlayerState = {
  title: string; name: string; team: string; status: HostState['status']; current: number; total: number; remainingMs: number;
  question: { text: string; options: string[] } | null; myAnswer: number | null; reveal: { answer: number; correct: boolean; points: number; ref: string | null } | null;
  myPoints: number; myRank: number; teams: HostState['teams']; top: HostState['top'];
};

export function TriviaPlayPage() {
  const { token = '' } = useParams();
  const state = usePolling((signal) => api.get<PlayerState>(`/bible/public/trivia/play/${token}`, signal), 1000, [token]);
  const [sent, setSent] = useState<{ idx: number; choice: number } | null>(null);
  const s = state.data;
  if (!s) return <div className="trivia-play">{state.error ? <EmptyState title="Ya no estás en este juego." /> : <Loading />}</div>;
  const mine = s.myAnswer ?? (sent && sent.idx === s.current ? sent.choice : null);
  async function answer(choice: number) {
    setSent({ idx: s!.current, choice });
    await api.post(`/bible/public/trivia/play/${token}/answer`, { choice }).catch(() => undefined);
    state.refresh();
  }
  return (
    <div className="trivia-play">
      <p className="trivia-play__who">
        {s.name} · {s.team} · <strong>{s.myPoints} pts</strong>
      </p>
      {s.status === 'lobby' ? <p className="trivia-play__wait">¡Listo! Espera a que empiece «{s.title}».</p> : null}
      {s.status === 'question' && s.question ? (
        mine === null ? (
          <>
            <p className="trivia-play__q">{s.question.text}</p>
            <div className="trivia__options">
              {s.question.options.map((o, k) => (
                <button key={k} type="button" className={`trivia__option trivia__option--${k}`} onClick={() => void answer(k)}>
                  <span className="trivia__letter">{LETTERS[k]}</span> {o}
                </button>
              ))}
            </div>
            <p className="muted">{Math.ceil(s.remainingMs / 1000)} s</p>
          </>
        ) : (
          <p className="trivia-play__wait">Respuesta enviada ({LETTERS[mine]}). Espera…</p>
        )
      ) : null}
      {s.status === 'reveal' && s.reveal ? (
        <div className={`trivia-play__result${s.reveal.correct ? ' trivia-play__result--ok' : ''}`}>
          <p className="trivia-play__big">{s.reveal.correct ? `¡Correcto! +${s.reveal.points}` : mine === null ? 'Sin respuesta' : 'Incorrecto'}</p>
          {s.question ? <p>Respuesta: {LETTERS[s.reveal.answer]}. {s.question.options[s.reveal.answer]}</p> : null}
          <p className="muted">Vas en el lugar {s.myRank}</p>
        </div>
      ) : null}
      {s.status === 'finished' ? (
        <div>
          <p className="trivia-play__big">🏆 Ganó {s.teams[0]?.name}</p>
          <p>
            Tu lugar: {s.myRank} · {s.myPoints} puntos
          </p>
          <Scoreboard teams={s.teams} top={s.top} />
        </div>
      ) : null}
    </div>
  );
}
